<template>
    <div :class="['patent-view__side-function', isCollapsed ? 'patent-view__side-function--collapse' : 'patent-view__side-function--expand']">
        <!--展开态-->
        <div v-show="!isCollapsed" class="patent-view__side-function-container--expand">
            <!--tabs-->
            <PtTabs
                id="side-function__tab"
                v-model="currentTab"
                :config="rightColumnTabConfig"
                active-color="#fff"
                class="patent-view__side-function__tab">
                <template #default="{ context }">
                    <ElTooltip
                        :visible-arrow="false"
                        :content="context.label"
                        :open-delay="POPPER_OPEN_DELAY"
                        placement="top">
                        <div class="patent-view__side-function__tab-item">
                            <PtIcon :icon="context.icon" class="patent-view__side-function__tab-icon" />
                            <span v-if="currentTab === context.action" class="patent-view__side-function__tab-text">{{ context.label }}</span>
                            <span v-if="context.action === 'comment' && commentLengthForDisplay" class="patent-view__side-function__tab-count">{{ commentLengthForDisplay }}</span>
                            <span v-if="context.action === 'customField' && selectedCustomFieldLengthForDisplay" class="patent-view__side-function__tab-count">{{ selectedCustomFieldLengthForDisplay }}</span>
                        </div>
                    </ElTooltip>
                </template>
            </PtTabs>
            <!--content-->
            <div class="patent-view__side-function-main-content">
                <!--patent-list-->
                <template v-if="hasPatentList">
                    <FamilyPatentsList
                        v-if="isFamilySearch"
                        v-show="currentTab === 'patentList'"
                        ref="patentList"
                        :patent-id="patentId"
                        :view-params="viewParams"
                        @pageInfoUpdate="pageInfoUpdate"
                    />
                    <PatentsList
                        v-else
                        v-show="currentTab === 'patentList'"
                        ref="patentList"
                        :patent-id="patentId"
                        :view-params="viewParams"
                        @pageInfoUpdate="pageInfoUpdate" />
                </template>

                <Higlighter v-show="currentTab === 'highlighter'" :patent-id="patentId" />

                <CustomFields
                    v-show="currentTab === 'customField'"
                    :key="taskId ? `${patentId}-${keyIndexForUpdate}` : patentId"
                    ref="customField"
                    :active-tab="currentTab"
                    :patent-id="patentId"
                    :space-id="workspaceId"
                    :folder-id="folderId"
                    :ws-view-type="wsViewType"
                    :task-id="taskId"
                    :task-type="taskType"
                    :is-task-done="currentTaskIsDone"
                    :is-loading-task="isLoadingTask"
                    :task-fields-list="customFieldDefsByTaskId"
                    @selectedLengthChange="val => selectedCustomFieldLength = val" />

                <Comments
                    v-if="currentTab === 'comment'"
                    ref="comments"
                    :key="`comments-${patentId}`"
                    :permission="commentPermission"
                    :patent-id="patentId"
                    :space-id="workspaceId"
                    :folder-id="folderId"
                    @commentLengthChange="val => commentLength = val">
                </Comments>

                <AddCommentDialog
                    :key="`comments-dialog-${patentId}`"
                    :comment-permission="commentPermission"
                    :patent-id="patentId"
                    :space-id="workspaceId"
                    :folder-id="folderId" />

                <AddQuickCommentDialog
                    :key="`quick-comments-dialog-${patentId}`"
                    :patent-id="patentId"
                    :space-id="workspaceId"
                    :folder-id="folderId" />

                <EditQuickCommentDialog
                    v-if="isShowingEditQuickComment"
                    :key="`edit-quick-comments-dialog-${patentId}`" />
            </div>
            <!--task-->
            <Task
                v-if="isHasTask"
                v-show="currentTab !== 'comment'"
                :patent-id="patentId"
                :view-params="viewParams"
                @statusChange="val => currentTaskIsDone = val"
                @customFieldDefsUpdate="updateCustomFieldDefsByTaskId"
                @loadingChange="val => isLoadingTask = val" />
        </div>
        <!--收缩态-->
        <div v-show="isCollapsed" class="patent-view__side-function-container--collapse">
            <div class="patent-view__side-function-collapse-icon-group">
                <ElTooltip
                    v-for="item in rightColumnTabConfig"
                    :key="item.action"
                    :visible-arrow="false"
                    :content="$t(item.tooltip)"
                    :disabled="!item.tooltip"
                    :open-delay="POPPER_OPEN_DELAY"
                    placement="left"
                >
                    <div
                        :class="{ 'patent-view__side-function-collapse-icon': true, 'patent-view__side-function-collapse-icon--disabled': item.disabled }"
                        @click="expandAndToggleTab(item.action, item.disabled)">
                        <PtIcon :icon="item.icon" />
                    </div>
                </ElTooltip>
            </div>
            <DataReport class="patent-view__side-function-collapse-report" />
        </div>
        <!--快捷键-->
        <Toggle
            :disable-pre="isDisablePre || !hasPatentList"
            :disable-next="isDisableNext || !hasPatentList"
            :is-collapsed="isCollapsed"
            @toggle="toggleClicked" />
    </div>
</template>

<script>
import $ from 'jquery';
import { get } from 'lodash';
import { Icon } from 'patsnap-biz';
import '@patsnap-ui/icon/assets/solid/Menu4.svg';
import '@patsnap-ui/icon/assets/solid/HighlightPen.svg';
import '@patsnap-ui/icon/assets/solid/tag.svg';
import '@patsnap-ui/icon/assets/solid/chat.svg';
import { Tooltip } from 'element-ui';
import PtTabs from '@/components/PtTabs';
import DataReport from '../../components/data-report/DataReport';
import PatentsList from './patents-list/PatentsList.vue';
import Higlighter from './higlighter';
import CustomFields from './custom-fields';
import Toggle from './patents-list/Toggle';
import Comments from './comments';
import Task from './task';
import AddCommentDialog from '../../components/dialog/CommentDialog/AddCommentDialog';
import AddQuickCommentDialog from '../../components/dialog/CommentDialog/AddQuickCommentDialog';
import EditQuickCommentDialog from '../../components/dialog/CommentDialog/EditQuickCommentDialog';
import WordingHandler from '../../utils/wording-handler';
import CommentService from '../../services/comment-service';
import { flattenComments } from '../../../../../components/components/comments/scripts/utils';
import { useFamilySearch } from '@/composables/use-family-search';
import FamilyPatentsList from '@/components/family-patents-list/FamilyPatentsList.vue';
import { usePermission } from '@/composables/use-permission';
import { usePatentsList } from '@/views/side-function-bar/patents-list/composables/use-patents-list';
import { COMMENT_OWNER_PERMISSION, POPPER_OPEN_DELAY, MAX_LIMIT_COMMENT_LENGTH } from '../../constants';
import { useCommentDialog } from '@/components/dialog/CommentDialog/composables/use-comment-dialog';

export default {
    name: 'SideFunctionBar',
    components: {
        PatentsList,
        Toggle,
        PtTabs,
        DataReport,
        PtIcon: Icon,
        Higlighter,
        CustomFields,
        Comments,
        AddCommentDialog,
        Task,
        ElTooltip: Tooltip,
        AddQuickCommentDialog,
        EditQuickCommentDialog,
        FamilyPatentsList
    },
    props: {
        viewParams: {
            type: Object,
            required: true
        },
        patentId: {
            type: String,
            required: true
        },
        workspaceId: {
            type: String,
            default: ''
        },
        folderId: {
            type: String,
            default: ''
        },
        pn: {
            type: String,
            required: true
        },
        isCollapsed: {
            type: Boolean,
            default: false
        }
    },
    setup() {
        const { isFamilySearch, isInFamilyOverview } = useFamilySearch();

        const { permissions } = usePermission();

        const { isShowPatentList, setPatentListVisible } = usePatentsList();

        const { isShowingEditQuickComment } = useCommentDialog();

        return {
            isFamilySearch,
            isInFamilyOverview,
            permissions,
            isShowPatentList,
            setPatentListVisible,
            isShowingEditQuickComment,
        };
    },
    data() {
        return {
            isDisablePre: false,
            isDisableNext: false,
            currentTab: 'patentList',
            commentPermission: {
                edit: true,
                read: true,
                isOwner: true,
            },
            preparingStatus: false,
            currentTaskIsDone: false,
            customFieldDefsByTaskId: [],
            keyIndexForUpdate: 0,
            commentLength: 0,
            selectedCustomFieldLength: 0,
            isLoadingTask: true,
            initCustomFieldComp: true,
        };
    },
    computed: {
        commentVisible () {
            return !this.preparingStatus && (this.commentPermission.edit || this.commentPermission.read);
        },
        customVisible () {
            return !this.preparingStatus;
        },
        acls() {
            return Object.keys(this.permissions).reduce((aclSet, curKey) => {
                // workspace内嵌时，只显示下载pdf和翻译
                if (this.permissions[curKey]) aclSet.add(curKey);
                return aclSet;
            }, new Set());
        },
        rightColumnTabConfig() {
            const vm = this;
            const tabList = [
                {
                    icon: 'SolidMenu4',
                    action: 'patentList',
                    label: this.$t('patent-view.patentList'),
                    hasPermission: this.hasPatentList,
                    tooltip: this.$t('patent-view.patentList')
                },
                {
                    icon: 'SolidHighlightPen',
                    action: 'highlighter',
                    label: this.$t('highlight.Highlighter'),
                    hasPermission: true,
                    tooltip: this.$t('highlight.Highlighter'),
                    disabled: this.isInFamilyOverview
                },
                {
                    icon: 'SolidTag',
                    action: 'customField',
                    label: this.$t('customFields.customField'),
                    tooltip: this.$t('customFields.customField'),
                    disabled: this.isInFamilyOverview,
                    ...{
                        get hasPermission () {
                            // customFieldFlag bo中的设置 用户无法使用自定义字段
                            return vm.customVisible && vm.acls.has('commentAndField') && !vm.acls.has('customFieldFlag');
                        }
                    }
                },
                {
                    icon: 'SolidChat',
                    action: 'comment',
                    label: this.$t('patent-view["comment-title"]'),
                    tooltip: this.$t('patent-view["comment-title"]'),
                    disabled: this.isInFamilyOverview,
                    ...{
                        get hasPermission () {
                            // commentFlag bo中的设置 用户无法使用comment
                            return vm.commentVisible && vm.acls.has('commentAndField') && !vm.acls.has('commentFlag');
                        }
                    }
                }
            ];
            return tabList.filter(item => item.hasPermission);
        },
        hasPatentList() {
            try {
                return window.location.search.split('$')[0].indexOf('page=') > -1;
            } catch (error) {
                return false;
            }
        },
        taskId() {
            return get(this.viewParams, 'task_id');
        },
        _type() {
            return get(this.viewParams, '_type');
        },
        wsViewType() {
            return get(this.viewParams, 'ws_view_type');
        },
        taskType() {
            return parseInt(get(this.viewParams, 'task_type', 1));
        },
        isHasTask() {
            return this._type === 'task' && this.taskId;
        },
        commentLengthForDisplay() {
            if (this.commentLength > 0) {
                return this.commentLength > MAX_LIMIT_COMMENT_LENGTH ? `${MAX_LIMIT_COMMENT_LENGTH}+` : this.commentLength;
            }
            return '';
        },
        selectedCustomFieldLengthForDisplay() {
            if (this.selectedCustomFieldLength > 0) {
                return this.selectedCustomFieldLength > MAX_LIMIT_COMMENT_LENGTH ? `${MAX_LIMIT_COMMENT_LENGTH}+` : this.selectedCustomFieldLength;
            }
            return '';
        }
    },
    watch: {
        async patentId() {
            if (this.commentVisible && this.acls.has('commentAndField') && !this.acls.has('commentFlag')) {
                // 切换右侧列表专利时，实时更新该数量
                // 最好的方法还是写一个空组件，组件内部专门用来发comment请求，key设置为patentId
                const _commentService = new CommentService(this.patentId, this.workspaceId, this.folderId);
                const originalCommentList = await _commentService.getCommentList();
                this.commentLength = flattenComments(originalCommentList).length;
            }
        },
        currentTab(val) {
            if (val === 'customField') {
                this.$refs.customField.getCustomFieldList();
            }
        }
    },
    created () {
        this.POPPER_OPEN_DELAY = POPPER_OPEN_DELAY;

        // 初始化请求标引自定义字段
        this.$nextTick(() => {
           this.$refs.customField.getCustomFieldList();
        })
    },
    async mounted () {
        WordingHandler.$on('addNewComment', this.scrollToWordingById);
        WordingHandler.$on('clickComment', this.scrollToWordingById);

        // 设置默认选中
        this.setDefaultTab();
        this.commentService = new CommentService(this.patentId, this.workspaceId, this.folderId);

        // 获取comment length，以便不点击comment按钮也显示数据
        if (this.commentVisible && this.acls.has('commentAndField') && !this.acls.has('commentFlag')) {
            const originalCommentList = await this.commentService.getCommentList();
            this.commentLength = flattenComments(originalCommentList).length;
        }

        if (!this.workspaceId || !this.folderId) return;
        this.preparingStatus = true;
        this.commentService.getCommentsPermission(this.workspaceId, this.folderId).then(r => {
            this.commentPermission.edit = get(r, 'comment.delete');
            this.commentPermission.read = get(r, 'comment.read');
            this.commentPermission.isOwner = COMMENT_OWNER_PERMISSION[get(r, 'is_owner')] || false;
        }).finally(() => {
            this.preparingStatus = false;
        });
    },
    beforeDestroy() {
        WordingHandler.$off('addNewComment', this.scrollToWordingById);
        WordingHandler.$off('clickComment', this.scrollToWordingById);
    },
    methods: {
        toggleCollapse() {
            window.rvTrack('Click Toggle Button', { source: this.$route.path, type: this.isShowPatentList === false ? 'close-to-open' : 'open-to-close' });
            this.setPatentListVisible(!this.isShowPatentList);
            this.$nextTick(() => $(window).trigger('resize'));
        },
        toggleClicked(type) {
            const patentList = get(this.$refs, 'patentList', {});
            switch (type) {
                case 'pre':
                    patentList.switchDelay && patentList.switchDelay(this.patentId, -1);
                    break;
                case 'next':
                    patentList.switchDelay && patentList.switchDelay(this.patentId, 1);
                    break;
                case 'collapes':
                    this.toggleCollapse();
                    break;
                default:
                    this.toggleCollapse();
                    break;
            }
        },
        pageInfoUpdate({ type, value }) {
            if (type === 'isFirst') this.isDisablePre = value;
            if (type === 'isLast') this.isDisableNext = value;
        },
        expandAndToggleTab(type, isDisabled) {
            if (isDisabled) return;
            this.currentTab = type;
            this.toggleCollapse();
        },
        setDefaultTab() {
            const hasCustomFieldTab = this.acls.has('commentAndField') && !this.acls.has('customFieldFlag');
            if (this.isHasTask && this.commentVisible && hasCustomFieldTab) {
                this.currentTab = 'customField';
            } else {
                this.currentTab = this.hasPatentList ? 'patentList' : 'highlighter';
            }
        },
        updateCustomFieldDefsByTaskId(val) {
            this.customFieldDefsByTaskId = val;
            this.keyIndexForUpdate++;
        },
        /* 新建划词注释时，点击划词注释时，打开侧边栏，切换 tab */
        scrollToWordingById (comment) {
            const wordId = typeof comment === 'string' ? comment : comment.wording_id;
            this.currentTab = 'comment';
            this.setPatentListVisible(true);
            this.$nextTick(() => {
                try {
                    this.$refs.comments.wordingClickedCallback(wordId);
                } catch (e) {
                    console.error(e);
                }
            });
        }
    }
};
</script>

<style lang="scss">
    .patent-view__side-function {
        height: 100%;
        width: 100%;

        .patent-view__side-function-container--expand, .patent-view__side-function-container--collapse {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
        }

        .patent-view__side-function-container--collapse {
            justify-content: space-between;
            padding: 4px 4px 16px 4px;
        }

        // 使用ID来增加样式权重，避免样式被chunk(deep view)中样式覆盖掉
        #side-function__tab.patent-view__side-function__tab {
            background-color: #f4f5f7;
            padding-top: 8px;
            border: 1px solid transparent;
            .pt__tab-item {
                min-width: 0;
                padding: 0 8px;
                margin-right: 0;
                background-color: #f4f5f7;
                border: 1px solid transparent;
                &--active {
                    border-color: transparent !important;
                }
                &--disabled {
                    cursor: not-allowed;
                    .patent-view__side-function__tab-icon {
                        color: #B3BAC5;
                    }
                }
                &:first-of-type {
                    margin-left: 8px;
                }
                &:last-of-type {
                    margin-right: 8px;
                }
            }
            .patent-view__side-function__tab-item {
                line-height: 30px;
            }
            .patent-view__side-function__tab-icon {
                vertical-align: middle;
                font-size: 20px;
            }
            .patent-view__side-function__tab-text {
                display: inline-block;
                vertical-align: middle;
                font-size: 12px;
                max-width: 75px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .patent-view__side-function__tab-count {
                color: #5E6C84;
                font-size: 12px;
            }
        }

        .patent-view__side-function-main-content {
            flex: 1;
            overflow: auto;
        }

        .patent-view__side-function-collapse-icon {
            text-align: center;
            border-radius: 2px;
            cursor: pointer;
            padding: 4px;
            margin-bottom: 8px;
            &:last-of-type {
                margin-bottom: 0;
            }
            &:hover {
                background-color: #ebecf0;
            }
            &--disabled {
                cursor: not-allowed;
                background-color: transparent !important;
                .patsnap-biz-icon {
                    color: #B3BAC5;
                }
            }
        }

        .patent-view__side-function-collapse-report {
            .elstc-btn {
                text-align: center;
                border-radius: 2px;
                width: 32px;
                padding: 4px;
            }
            .eb-pre-text {
                display: none;
            }
        }

        &--collapse {
            background-color: #f4f5f7;
            .patent-list-toggle__item, .patent-list-toggle__item--disabled {
                background-color: #f4f5f7 !important;
            }
        }
    }
</style>
